import { Box, Button, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useNavigate, useParams } from "react-router-dom";
import { PaymentProfileService } from "subModule/src/core/services";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { GetAdjustedInvoicesPaymentResponse, PaymentsResponse } from "subModule/src/services/payment/interface";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as PaymentIcon } from "assets/SideBarIcons/money-transfer.svg";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
const ViewPayment: React.FC = () => {
    const params = useParams();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
        sectionLabelKey: "customers",
        moduleLabelKey: "payments",
        moduleHref: "/payments",
        entityFallbackLabelKey: "paymentNo",
        isEdit: !!params?.id,
        action: "view",
    });
    const branding = useSelector(brandingInfo);
    const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
    const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(true);
    const [payment, setPayment] = useState<PaymentsResponse>();
    const [invoices, setInvoices] = useState<any[]>([]);
    useEffect(() => {
        if (loading) getAdjustedInvoices();
    }, []);
    const getAdjustedInvoices = () => {
        PaymentProfileService.getAdjustedInvoicesPayment(Number(params?.id)).then((res: GetAdjustedInvoicesPaymentResponse) => {
            if (res.success) {
                setPayment(res?.data?.payment || {});
                if(res?.data?.payment?.adjustmentType === 592) setInvoices(res?.data?.invoices || []);
                captureLabel(
                    res?.data?.payment?.paymentNo != null
                        ? String(res.data.payment.paymentNo)
                        : null,
                );
                setLoading(false);
            } else {
                setLoading(false);
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err: any) => { console.error("===", err); });
    }
    const columns = [
        { id: 'invoiceNo', label: t('invoiceNo'), numeric: false },
        { id: 'createdAt', label: t('Created'), numeric: false },
        { id: 'adjustedAmount', label: t('adjustedAmount'), numeric: false },
    ];
    return (
        <>
            <Box className="hide-on-print">
                <ComponentHeaderTwo
                    breadcrumbItems={breadcrumbItems}
                    showBtn={true}
                    showSaveBtn={false}
                    onClick={() => navigate("/payments")}
                    onSubmit={() => {}}
                    heading={t("payments")}
                    icon={<PaymentIcon height={28} width={28} />}
                    btnText={"back"}
                    btnType={"back"}
                    showCreateBtn={false}
                />
            </Box>
            {loading ? <Loader /> : <Box>
            <Box className='max-w-[1100px] bg-white py-[10px] px-[20px] rounded-[8px] block mx-auto hide-on-print mb-4'>
                <Box className='flex justify-between items-center' id='form'>
                    <Typography className="font-Saira text-[12px] font-semibold" style={{ color: themeColors?.primary }}>{t('Payment')}</Typography>
                    <Box className='flex gap-2'>
                        <Button className="secondary" variant="contained" style={{ color: themeColors?.primary }} disableElevation onClick={() => navigate(-1)}>{t('back')}</Button>
                        <Button className="primary" variant="contained" style={{ background: themeColors?.primary }} disableElevation onClick={() => window.print()}>{t('Print')}</Button>
                    </Box>
                </Box>
            </Box>
            <Box id="print-root" className='max-w-[1100px] bg-white p-[20px] rounded-[8px] block mx-auto'>
                <Box className='relative flex justify-between mb-3 items-center'>
                    <Box>
                        <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('Client')}: </span>
                            <span className="font-bold">{payment?.clientName || ''}</span>
                        </Typography>
                        {payment?.accountTitle ? <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('ACCOUNT')}: </span>
                            <span className="font-bold">{payment?.accountTitle || ''}</span>
                        </Typography> : ''}
                    </Box>
                    {branding?.attributes?.logo ? <Box className="absolute left-1/2 -translate-x-[70%]">
                        <img src={branding?.attributes?.logo || ""} style={{
                                height: "60px", objectFit: "contain",
                            }}
                        />
                    </Box> : null}
                    <Box>
                        <Box className='block py-1 px-2 rounded-[8px]' style={{ background: themeColors?.appBg }}>
                            <Typography className="font-Saira text-[12px]">
                                <span className="font-bold" style={{ color: themeColors?.secondary }}>{t('Payment')} # </span>
                                <span className="font-semibold">{payment?.paymentNo || ''}</span>
                            </Typography>
                        </Box>
                        {payment?.referenceNo ?<Typography className="font-Saira text-[12px] text-end font-semibold">
                            <span className="font-bold" style={{ color: themeColors?.secondary }}>{t('referenceNo')} </span>
                            <span className="font-semibold">{payment?.referenceNo || ''}</span>
                        </Typography> : null}
                    </Box>
                </Box>
                <Box className='flex justify-between items-center py-3 px-2 rounded-[8px] mb-2' style={{ background: themeColors?.appBg }}>
                    <Typography className="font-Saira text-[12px] text-left">
                        <span className="italic font-medium">{t('PaymentMode')}: </span>
                        <span className="font-bold">{payment?.paymentModeTitle || ''}</span>
                    </Typography>
                    <Box className="flex gap-3 items-center">
                        <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('adjustmentType')}</span>
                            <span> : </span>
                            <span className="font-bold">{[
                                    { value: 590, label: t("Advance") },
                                    { value: 591, label: t("FIFO") },
                                    { value: 592, label: t("MANUAL") },
                                ].find((option: any) => option.value === payment?.adjustmentType)?.label || ''}
                            </span>
                        </Typography>
                        <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('Payment') + ' ' + t('Date')}</span>
                            <span> : </span>
                            <span className="font-bold">{payment?.paymentDate?.split('T')[0] || ''}</span>
                        </Typography>
                        <Typography className="font-Saira text-[12px] text-left block py-1 px-3 rounded-[8px]" style={{ background: themeColors?.secondary + '33' }}>
                            <span className="italic font-medium">{t('recipentDate')}</span>
                            <span> : </span>
                            <span className="font-bold">{payment?.recipientDate?.split('T')[0] || ''}</span>
                        </Typography>
                    </Box>
                </Box>
                {invoices && invoices?.length ? <>
                    <Typography className="font-Saira text-[12px] font-semibold text-[#333843] text-left mb-2">{t('adjustedInvoices')}</Typography>
                    <Box className='w-[100%] mb-2' id="inventorytable">
                        <TableContainer>
                            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={'small'}>
                                <TableHead>
                                    <TableRow sx={{ borderBottom: `1px solid #B8BDCB` }}>
                                        {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                                            <TableCell
                                                key={column.id}
                                                align={column.numeric ? 'right' : 'left'}
                                                padding={'normal'}
                                                sortDirection={false}
                                                className="font-bold text-center"
                                            >{column.label}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody sx={{ '& tr:nth-child(odd)': { background: `transparent !important` }, '& tr': { borderBottom: `1px solid #B8BDCB` }, '& tr:last-child': { borderBottom: 0 } }}>
                                    {(invoices || []).map((row, index) => {
                                        const labelId = `enhanced-table-checkbox-${index}`;
                                        return (
                                            <TableRow hover tabIndex={-1} key={row.id} sx={{ height: '36px' }}>
                                                {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                                                    <TableCell
                                                        key={column.id}
                                                        align={column.numeric ? 'right' : 'left'}
                                                        padding={'normal'}
                                                        component={column.id === 'itemName' ? 'th' : undefined}
                                                        id={column.id === 'itemName' ? labelId : undefined}
                                                        scope={column.id === 'itemName' ? 'row' : undefined}
                                                        className="text-center"
                                                    >{column.id === 'createdAt' ? 
                                                            <span>
                                                                {(() => {
                                                                    const date = new Date(row?.createdAt);
                                                                    return isNaN(date.getTime())
                                                                    ? "-"
                                                                    : new Intl.DateTimeFormat(i18n.language, {
                                                                        year: "numeric",
                                                                        month: "long",
                                                                        day: "numeric",
                                                                        hour: "2-digit",
                                                                        minute: "2-digit",
                                                                        second: "2-digit",
                                                                        numberingSystem:
                                                                        i18n.language === "ur" || i18n.language === "ar"
                                                                            ? "arab"
                                                                            : "latn",
                                                                    }).format(date);
                                                                })()}
                                                            </span>
                                                        : (row as any)[column.id]
                                                        }</TableCell>
                                                ))}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </> : null}
                <Box className='flex justify-end mt-4 mb-3'>
                    <Box className="w-[360px]">
                        <Divider className="bg-[#AEB3C4] mb-2" />
                        <Box className='flex justify-end mb-[5px]'>
                            <Typography className="font-Saira text-[12px] text-left flex">
                                <span className="font-medium">{t('unusedAmount')}: </span>
                                <p className="font-bold ms-1">
                                    <span className="uppercase ms-1 font-semibold" style={{ color: themeColors?.primary }}>{loggedInUserCurrency}</span>
                                    <span className="italic font-bold ms-1">{new Intl.NumberFormat('en-US', {}).format(payment?.balanceAmount || 0) || 0}</span>
                                </p>
                            </Typography>
                        </Box>
                        <Box className='flex justify-end mb-[5px]'>
                            <Typography className="font-Saira text-[12px] text-left flex">
                                <span className="font-medium">{t('paidAmount')}: </span>
                                <p className="font-bold ms-1">
                                    <span className="uppercase ms-1 font-semibold" style={{ color: themeColors?.primary }}>{loggedInUserCurrency}</span>
                                    <span className="italic font-bold ms-1">{new Intl.NumberFormat('en-US', {}).format(payment?.amount || 0) || 0}</span>
                                </p>
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                {payment?.remarks ? <Box>
                    <Typography className="font-Saira text-[12px] font-semibold text-[#171826] text-left">{t("remarks")}</Typography>
                    <Typography className="font-Saira text-[12px] text-[#171826] text-left">{payment?.remarks}</Typography>
                </Box> : null}
            </Box>
        </Box>}
        </>
    )
}

export default ViewPayment;